import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { formatCurrency, formatDate, API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, History, Star } from "lucide-react";

const emptyForm = { name: "", nif: "", phone: "", email: "", address: "", notes: "" };

export default function SuppliersPage() {
  const { token } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [historyModal, setHistoryModal] = useState(false);
  const [history, setHistory] = useState(null);
  const [evalModal, setEvalModal] = useState(false);
  const [evalSupplier, setEvalSupplier] = useState(null);
  const [evalData, setEvalData] = useState(null);
  const [evalForm, setEvalForm] = useState({ rating: "5", comment: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchSuppliers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/suppliers`, { headers, withCredentials: true });
      setSuppliers(res.data);
    } catch { toast.error("Erro ao carregar fornecedores"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API_URL}/suppliers/${editing.supplier_id}`, form, { headers, withCredentials: true });
        toast.success("Fornecedor atualizado");
      } else {
        await axios.post(`${API_URL}/suppliers`, form, { headers, withCredentials: true });
        toast.success("Fornecedor criado");
      }
      setModal(false); setEditing(null); setForm(emptyForm);
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao guardar"); }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Remover fornecedor "${s.name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/suppliers/${s.supplier_id}`, { headers, withCredentials: true });
      toast.success("Fornecedor removido");
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover"); }
  };

  const openHistory = async (s) => {
    try {
      const res = await axios.get(`${API_URL}/suppliers/${s.supplier_id}/history`, { headers, withCredentials: true });
      setHistory({ supplier: s, ...res.data });
      setHistoryModal(true);
    } catch { toast.error("Erro ao carregar histórico"); }
  };

  const openEvaluations = async (s) => {
    try {
      const res = await axios.get(`${API_URL}/suppliers/${s.supplier_id}/evaluations`, { headers, withCredentials: true });
      setEvalData(res.data);
      setEvalSupplier(s);
      setEvalForm({ rating: "5", comment: "" });
      setEvalModal(true);
    } catch { toast.error("Erro ao carregar avaliações"); }
  };

  const submitEvaluation = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/suppliers/${evalSupplier.supplier_id}/evaluations`, {
        rating: parseInt(evalForm.rating),
        comment: evalForm.comment,
      }, { headers, withCredentials: true });
      toast.success("Avaliação registada");
      const res = await axios.get(`${API_URL}/suppliers/${evalSupplier.supplier_id}/evaluations`, { headers, withCredentials: true });
      setEvalData(res.data);
      setEvalForm({ rating: "5", comment: "" });
      fetchSuppliers();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Fornecedores</h1>
          <p className="text-muted-foreground">Gestão de fornecedores e histórico de compras</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(emptyForm); setModal(true); }} data-testid="add-supplier-btn">
          <Plus className="w-4 h-4 mr-2" /> Novo Fornecedor
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : suppliers.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nenhum fornecedor registado
        </CardContent></Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {suppliers.map((s) => (
            <Card key={s.supplier_id} data-testid={`supplier-card-${s.supplier_id}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold">{s.name}</p>
                    {s.nif && <p className="text-xs text-muted-foreground">NIF: {s.nif}</p>}
                  </div>
                  <div className="flex items-center gap-1">
                    {s.classification && (
                      <Badge className={s.classification === "A" ? "bg-emerald-500/20 text-emerald-600" : s.classification === "B" ? "bg-amber-500/20 text-amber-600" : "bg-red-500/20 text-red-500"}>
                        Classe {s.classification} {s.avg_rating ? `· ${s.avg_rating}★` : ""}
                      </Badge>
                    )}
                    <Badge variant="outline">{s.is_active === false ? "Inativo" : "Ativo"}</Badge>
                  </div>
                </div>
                {s.phone && <p className="text-sm text-muted-foreground">{s.phone}</p>}
                {s.email && <p className="text-sm text-muted-foreground">{s.email}</p>}
                <div className="flex gap-2 pt-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={() => openHistory(s)} data-testid={`supplier-history-${s.supplier_id}`}>
                    <History className="w-4 h-4 mr-1" /> Histórico
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEvaluations(s)} data-testid={`supplier-eval-${s.supplier_id}`}>
                    <Star className="w-4 h-4 mr-1" /> Avaliar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(s); setForm({ ...emptyForm, ...s }); setModal(true); }}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => handleDelete(s)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar Fornecedor" : "Novo Fornecedor"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label>Nome *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>NIF</Label>
                <Input value={form.nif || ""} onChange={(e) => setForm({ ...form, nif: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Telefone</Label>
                <Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Email</Label>
              <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Endereço</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Notas</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="save-supplier-btn">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={evalModal} onOpenChange={setEvalModal}>
        <DialogContent className="max-w-md" data-testid="supplier-eval-modal">
          <DialogHeader><DialogTitle>Avaliação — {evalSupplier?.name}</DialogTitle></DialogHeader>
          <div className="flex items-center gap-4 p-3 rounded-lg bg-muted">
            <div>
              <p className="text-xs text-muted-foreground">Média</p>
              <p className="text-xl font-bold">{evalData?.avg_rating || "-"} ★</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Classificação</p>
              <p className="text-xl font-bold">{evalData?.classification || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Avaliações</p>
              <p className="text-xl font-bold">{evalData?.evaluations?.length || 0}</p>
            </div>
          </div>
          <form onSubmit={submitEvaluation} className="space-y-2">
            <div className="flex gap-2">
              <select
                className="border rounded-md px-2 py-1.5 text-sm bg-background"
                value={evalForm.rating}
                onChange={(e) => setEvalForm({ ...evalForm, rating: e.target.value })}
                data-testid="eval-rating-select"
              >
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} ★</option>)}
              </select>
              <Input className="flex-1" placeholder="Comentário" value={evalForm.comment} onChange={(e) => setEvalForm({ ...evalForm, comment: e.target.value })} />
              <Button type="submit" size="sm" data-testid="submit-eval-btn"><Star className="w-4 h-4 mr-1" /> Avaliar</Button>
            </div>
          </form>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {(evalData?.evaluations || []).map((ev) => (
              <div key={ev.evaluation_id} className="flex justify-between items-center p-2 rounded border text-sm">
                <span>{"★".repeat(ev.rating)} {ev.comment}</span>
                <span className="text-xs text-muted-foreground">{ev.created_by_name}</span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyModal} onOpenChange={setHistoryModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Histórico — {history?.supplier?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Total Comprado</p>
                <p className="font-bold font-mono">{formatCurrency(history?.total_spent)}</p>
              </CardContent></Card>
              <Card><CardContent className="p-3 text-center">
                <p className="text-xs text-muted-foreground">Nº Compras</p>
                <p className="font-bold">{history?.purchase_count || 0}</p>
              </CardContent></Card>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {(history?.purchases || []).map((p) => (
                <div key={p.purchase_id} className="flex justify-between items-center p-2 rounded border text-sm">
                  <div>
                    <p className="font-mono font-medium">{p.number}</p>
                    <p className="text-xs text-muted-foreground">{formatDate(p.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono">{formatCurrency(p.total)}</p>
                    <Badge variant="outline" className="text-xs">{p.status === "received" ? "Recebida" : p.status === "cancelled" ? "Cancelada" : "Pendente"}</Badge>
                  </div>
                </div>
              ))}
              {(history?.purchases || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Sem compras registadas</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
